import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState
} from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  Icon,
  Icons,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch
} from '@clientbase/clientbase-library'
import { Box, Grid, Tooltip, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import {
  GROUP_UNAVAILABLE_HINT,
  isGroupMode,
  isPathAllowedInGroup
} from 'lib/merchant-group'
import mixpanel from 'mixpanel-browser'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { parseCookies, setCookie } from 'nookies'
import InvoiceIcon from 'templates/Invoice/components/InvoiceIcon'
import { roleMenuConfigs } from 'templates/Layout/Layout'

import IconContainer from 'components/IconContainer/IconContainer'
import RoundedItem from 'components/RoundedItem'

import decodeBase64 from 'utils/decode-base64-to-string'
import getCurrentMonth from 'utils/get-current-month'
import { SESSION_COOKIE_OPTIONS } from 'utils/sessionCookie'
import { FeatureTabs, settingsTabsParams } from 'utils/tabs'
import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

import * as S from './Shortcuts.styles'

interface shortcutOption {
  id: string
  name: string
  icon: Icons | JSX.Element
  iconSelected?: JSX.Element
  iconModal?: JSX.Element
  route: string
  isNew?: boolean
}

const shortcutOptions: shortcutOption[] = [
  {
    id: `charges`,
    name: `Cobranças`,
    icon: `event`,
    route: `/cobrancas?tab=${
      FeatureTabs.CHARGES
    }&due_date_between=${getCurrentMonth().join(`|`)}`
  },
  {
    id: `customers`,
    name: `Clientes`,
    icon: `person`,
    route: `/clientes?tab=${FeatureTabs.CUSTOMERS}`
  },
  {
    id: `contracts`,
    name: `Contratos`,
    icon: `assignment`,
    route: `/comercial?tab=${FeatureTabs.CONTRACTS}`
  },
  {
    id: `recurrences`,
    name: `Recorrências`,
    icon: `update`,
    route: `/cobrancas?tab=${FeatureTabs.RECURRENCES}`
  },
  {
    id: `communication`,
    name: `Comunicação`,
    icon: `sms`,
    route: `/clientes?tab=${FeatureTabs.COMMUNICATION}`
  },
  {
    id: `imports`,
    name: `Importações`,
    icon: `noteAdd`,
    route: `/settings?tab=${settingsTabsParams.IMPORTS}`
  },
  {
    id: `transfers`,
    name: `Transferências`,
    icon: `compareArrows`,
    route: `/cobrancas?tab=${
      FeatureTabs.TRANSFERS
    }&transfer_date_between=${getCurrentMonth().join(`|`)}`
  },
  {
    id: `portal`,
    name: `Personalizar`,
    icon: `edit`,
    route: `/clientes?tab=${FeatureTabs.PORTAL}`
  },
  {
    id: `nfs`,
    name: `NFS-e`,
    icon: <InvoiceIcon />,
    route: `/cobrancas?tab=${
      FeatureTabs.NFS
    }&created_at_between=${getCurrentMonth().join(`|`)}`
  },
  {
    id: `products`,
    name: `Planos`,
    icon: `star`,
    route: `/comercial?tab=${FeatureTabs.PRODUCTS}`
  },
  {
    id: `payment_link`,
    name: `Link de Contratação`,
    icon: `creditCardChecked`,
    route: `/comercial?tab=${FeatureTabs.PAYMENT_LINK}`
  }
]

// O back gravava 'clients' como atalho padrão; o id na tela virou 'customers'
// (SC-7024). Quem nunca editou os atalhos ainda tem 'clients' no banco/cookie.
const LEGACY_SHORTCUT_IDS: Record<string, string> = { clients: 'customers' }

const normalizeShortcuts = (shortcuts: string[]): string[] =>
  Array.from(new Set(shortcuts.map((id) => LEGACY_SHORTCUT_IDS[id] ?? id)))

interface ShortcutsProps {
  setIsExpandedMobileMenu?: Dispatch<SetStateAction<boolean>>
}

const Shortcuts = ({ setIsExpandedMobileMenu }: ShortcutsProps) => {
  const { push, asPath } = useRouter()

  const currentPath = asPath.split('&')[0]

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeShortcuts, setActiveShortcuts] = useState<string[]>([])
  const [temporaryShortcuts, setTemporaryShortcuts] = useState<string[]>([])

  const getShortcuts = async () => {
    try {
      const { ['nextauth.shortcuts']: shortcuts } = parseCookies()

      let shortcutsFromCookies: string[] = []

      if (!shortcuts) {
        // Endpoint do próprio usuário: a listagem /v1/merchant_users exige
        // users:show e dava "Sem permissão" para quem não é admin.
        const response = await DataService({
          url: `/v1/merchant_users/shortcuts`,
          type: 'GET'
        })

        const userShortcuts = response?.data?.shortcuts

        if (userShortcuts && typeof userShortcuts === 'string') {
          shortcutsFromCookies = userShortcuts.split(';').filter(Boolean)
          handleSaveShortcutsCookies(shortcutsFromCookies.join(';'))
        }
      } else {
        shortcutsFromCookies = shortcuts.split(';').filter(Boolean)
      }

      // Valida que todos os itens são strings válidas
      const validShortcuts = normalizeShortcuts(
        shortcutsFromCookies.filter(
          (shortcut) => typeof shortcut === 'string' && shortcut.trim() !== ''
        )
      )

      setActiveShortcuts(validShortcuts)
      setTemporaryShortcuts(validShortcuts)
    } catch (error) {
      console.error('Erro ao carregar atalhos:', error)
      setActiveShortcuts([])
      setTemporaryShortcuts([])
    }
  }

  useEffect(() => {
    getShortcuts()
  }, [])

  const handleSaveShortcutsCookies = (shortcutsString: string) => {
    setCookie(
      undefined,
      'nextauth.shortcuts',
      shortcutsString,
      SESSION_COOKIE_OPTIONS
    )
  }
  const mixpanelTyped = mixpanel as unknown as { __loaded: string }
  const handleShortcutsSubmit = async () => {
    const shortcutsString = temporaryShortcuts.join(';')

    const id = toast.loading('Atualizando atalhos...')

    // PATCH /v1/merchant_users/:uuid exige users:manage (só admin); o
    // endpoint de atalhos grava só a preferência do próprio usuário.
    const response = await DataService({
      type: 'PATCH',
      data: { shortcuts: shortcutsString },
      url: `/v1/merchant_users/shortcuts`
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error
        ? errorMessage
        : 'Atalhos atualizados com sucesso!',
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    // Falhou (ex.: 403): o toast acima já mostra o erro. Não grava cookie nem
    // atualiza a barra, senão o atalho aparece até o próximo login e some.
    if (response.error) return

    handleSaveShortcutsCookies(shortcutsString)

    setIsModalOpen(false)
    setActiveShortcuts(temporaryShortcuts)
  }

  // Os switches editam um rascunho. Ao abrir, ele volta ao que está na barra: assim
  // Cancelar, o X ou clicar fora descartam o que foi ligado/desligado, e um Salvar
  // depois não leva junto uma mudança que a pessoa tinha cancelado.
  const handleOpenModal = () => {
    setTemporaryShortcuts(activeShortcuts)
    setIsModalOpen(true)
  }

  const changeShortcutState = (
    event: ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    if (event.target.checked && !temporaryShortcuts.includes(id)) {
      setTemporaryShortcuts((previous) => [...previous, id])
    }

    if (!event.target.checked && temporaryShortcuts.includes(id)) {
      const tempArray = [...temporaryShortcuts]
      tempArray.splice(tempArray.indexOf(id), 1)
      setTemporaryShortcuts(tempArray)
    }
  }

  const handleClick = (
    event: React.MouseEvent<HTMLDivElement>,
    route: string
  ) => {
    const mouseEvent = event as React.MouseEvent
    const isNewTab = mouseEvent.ctrlKey || mouseEvent.metaKey

    if (isNewTab) {
      window.open(route, '_blank')
    } else {
      push(route)
    }
    !!setIsExpandedMobileMenu && setIsExpandedMobileMenu(false)
  }
  const { ['nextauth.role']: role } = parseCookies()
  const userRole = decodeBase64(role || '')
  const config = roleMenuConfigs[userRole || 'guest']
  const permissions = config.menuEntries
    .map((menu) => ('subheaders' in menu ? menu.subheaders : []))
    .flat()
    .map((item) => item.toLowerCase())

  return (
    <S.Wrapper>
      <S.ActiveShortcuts>
        <RoundedItem
          tooltipTitle="Adicionar atalhos"
          icon="add"
          isAddIconItem={true}
          onClick={handleOpenModal}
          size="xs"
          color={theme.palette.neutral[400]}
          name="Atalho"
        />
        {shortcutOptions.map((shortcut) => {
          if (
            !activeShortcuts.includes(shortcut.id) ||
            !permissions.includes(shortcut.id)
          ) {
            return null
          }
          // SC-10999: atalho para tela por empresa fica esmaecido na visão
          // multi-empresas, com o mesmo aviso do menu lateral
          const groupUnavailable =
            isGroupMode() && !isPathAllowedInGroup(shortcut.route)
          const link = (
            <Link
              href={shortcut.route}
              key={shortcut.id}
              onClick={(e) => e.preventDefault()}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <RoundedItem
                name={shortcut.name}
                icon={shortcut.icon}
                isNew={shortcut.isNew}
                isActive={
                  ['Transferências', 'Cobrança'].includes(shortcut.name)
                    ? shortcut.route.includes(currentPath)
                    : asPath == shortcut.route
                }
                onClick={(e) => {
                  mixpanelTyped.__loaded &&
                    // SC-10694: ver Tabs.tsx — `menu: 'null'` literal sujava a
                    // propriedade. Atalho não vem de um menu; `origem` basta.
                    mixpanel.track('Clique no menu', {
                      origem: 'atalhos',
                      submenu: shortcut.name
                    })
                  handleClick(
                    e as unknown as React.MouseEvent<HTMLDivElement>,
                    shortcut.route
                  )
                }}
                size="xs"
                color={theme.palette.neutral[400]}
              />
            </Link>
          )
          if (!groupUnavailable) return link
          return (
            <Tooltip key={shortcut.id} title={GROUP_UNAVAILABLE_HINT} arrow>
              <Box sx={{ opacity: 0.45 }}>{link}</Box>
            </Tooltip>
          )
        })}
      </S.ActiveShortcuts>

      <Modal
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        size="large"
        width="100%"
        maxWidth="50vw"
        maxHeight="90vh"
        height="auto"
      >
        <ModalHeader onClose={() => setIsModalOpen(false)}>
          <S.ModalTitle>
            <IconContainer icon="faCirclePlus" />
            <Typography variant="mdBold">Adicionar atalhos</Typography>
          </S.ModalTitle>
        </ModalHeader>

        <ModalContent>
          <Grid container spacing={2}>
            {shortcutOptions.map(({ id, name, icon }) => (
              <Grid item key={name} lg={2.4} md={3} sm={4} xs={4}>
                <S.ToggleShortcutBox isActive={temporaryShortcuts.includes(id)}>
                  {typeof icon === 'object' ? (
                    <Box
                      bgcolor="#1E1E1E"
                      borderRadius="50%"
                      padding={2}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {React.cloneElement(icon, { color: 'white', width: 28 })}
                    </Box>
                  ) : (
                    <Icon
                      icon={icon}
                      fontSize="large"
                      style={{
                        backgroundColor: '#1E1E1E',
                        borderRadius: '50%',
                        padding: 12,
                        color: 'white'
                      }}
                    />
                  )}
                  <Typography variant="xsLight" textAlign="center">
                    {name.length > 12 ? name.substring(0, 12) + '.' : name}
                  </Typography>
                  <Switch
                    checked={temporaryShortcuts.includes(id)}
                    onChange={(e) => {
                      changeShortcutState(e, id)
                    }}
                  />
                </S.ToggleShortcutBox>
              </Grid>
            ))}
          </Grid>
        </ModalContent>

        <ModalFooter>
          <Button variantButton="neutral" onClick={() => setIsModalOpen(false)}>
            Cancelar
          </Button>
          <Button variantButton="primary" onClick={handleShortcutsSubmit}>
            Salvar
          </Button>
        </ModalFooter>
      </Modal>
    </S.Wrapper>
  )
}

export default Shortcuts
